import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import Heading from '@cotyorg/ccx-atoms/Heading';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import ProductDetailsAccordion from '@cotyorg/ccx-molecules/ProductDetails/components/ProductDetailsAccordion';
import Breadcrumbs from '@cotyorg/ccx-molecules/Breadcrumbs';
import { trackSelectedShadeEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/product';
import dispatch from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/dispatch';
import MikMakWidget from '@cotyorg/ccx-molecules/MikMakWidget';
import ProductSwatch from '../../../ProductSwatch/components/ProductSwatchSizes';

const ProductInfo = ({
  breadcrumb,
  sectionHeadings,
  productDetails,
  categoryTitle,
  subcategoryTitle,
  productSwatch,
  shapes,
  showRating = true,
  customRetailer,
  selectedSKU,
  channelAdvisorWidget,
  hidePrice,
  vtoButton,
  currentBreakpoint,
  mikMakConfig,
}) => {
  const [selectedShape, setSelectedShape] = useState('all');
  const [isMikMak, setIsMikMak] = useState(false);
  const [viewType, setViewType] = useState('texture');

  const getSwatchDescription = ({ selectedSku, swatches }) => {
    const swatch = swatches.swatches.find(({ sku }) => sku === selectedSku);
    return R.propOr('', 'description')(swatch);
  };

  const getSwatchName = ({ selectedSku, swatches }) => {
    const swatch = swatches.swatches.find(({ sku }) => sku === selectedSku);
    return R.propOr('', 'itemName')(swatch);
  };

  const uniqueShapes = new Map();
  shapes.forEach((item) => {
    item.shapes.forEach((shape) => {
      uniqueShapes.set(shape.code, shape.title);
    });
  });

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      setIsMikMak(false);
    }
  };

  const trackSelectedShapeEvent = ({
    shape,
    name,
    type,
    location = 'shape',
  }) => {
    const eventName = 'shapeSelected';

    const detail = {
      eventInfo: {
        eventAction: 'Shape Selected',
        eventName,
        eventType: 'userEvent',
        productInfo: {
          shape,
          name,
          type,
          location,
        },
      },
    };

    dispatch({
      eventName,
      detail,
    });
  };

  const trackBuyNowEvent = () => {
    const eventName = 'buyNow';

    const detail = {
      eventInfo: {
        eventAction: 'shop now click',
        eventType: 'userEvent',
      },
    };

    dispatch({
      eventName,
      detail,
    });
  };

  const handleShapeClick = (code, title) => {
    const swatches = document.querySelectorAll('.swatches__swatch');

    swatches.forEach((swatch) => {
      const swatchClasses = swatch.className.split(' ');

      if (code === 'all' || swatchClasses.includes(code)) {
        swatch.style.display = 'inline-block';
      } else {
        swatch.style.display = 'none';
      }
    });

    setSelectedShape(code);

    trackSelectedShapeEvent({
      shape: title,
      name: R.compose(R.toLower(), R.prop('title'))(productDetails),
      type: R.toLower(subcategoryTitle),
      location: 'button',
    });
  };

  useEffect(() => {
    handleShapeClick(selectedShape, selectedShape);
  }, [viewType]);

  return (
    <div className="product-info">
      <div className="product-info__inner">
        <OptionalSection
          renderCondition={showRating}
          section={() => (
            <div
              data-bv-show="rating_summary"
              data-bv-product-id={R.prop('bvid', productDetails)}
            />
          )}
        />
        <Breadcrumbs items={breadcrumb} />

        <Heading extraClass="product-info__tag-heading" level={4}>
          {R.path(['productTag'], productDetails)}
        </Heading>

        <Heading
          data-test="product-heading"
          extraClass="h2 product-info__heading"
          level={1}
          itemProp="name"
          dangerouslySetInnerHTML={{
            __html: getSwatchName({
              selectedSku: selectedSKU,
              swatches: productSwatch,
            }),
          }}
        />

        <Heading
          level={2}
          extraClass="product-info__description"
          data-test="product-description"
          dangerouslySetInnerHTML={{
            __html: `${getSwatchDescription({
              selectedSku: selectedSKU,
              swatches: productSwatch,
            })} ${R.prop('description_with_html', productDetails)}`,
          }}
        />
      </div>
      <div className="views__wrapper">
        {R.prop('view_hand_image', productDetails) &&
          R.prop('view_texture_image', productDetails) && (
            <div className="views">
              <span>{R.prop('viewText', productDetails)}</span>
              <button
                onClick={() => {
                  setViewType('hand');
                }}
                className={viewType === 'hand' ? 'selected' : null}
                type="button"
              >
                <img src={R.prop('view_hand_image', productDetails)} alt="" />
              </button>
              <button
                onClick={() => {
                  setViewType('texture');
                }}
                className={viewType === 'texture' ? 'selected' : null}
                type="button"
              >
                <img
                  src={R.prop('view_texture_image', productDetails)}
                  alt=""
                />
              </button>
            </div>
          )}

        <div className="shapes">
          <span>{R.prop('shapeText', productDetails)}</span>
          <div>
            <button
              onClick={() => handleShapeClick('all', 'all')}
              className={selectedShape === 'all' ? 'selected' : ''}
              type="button"
            >
              {R.prop('showAllShapesLabel', productDetails)}
            </button>
            {Array.from(uniqueShapes).map(([code, title]) => (
              <button
                className={selectedShape === code ? 'selected' : ''}
                key={code}
                type="button"
                href={`#${code}`}
                onClick={() => handleShapeClick(code, title)}
              >
                {title}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ProductSwatch
        onChange={({ shade, sku }) => {
          trackSelectedShadeEvent({
            shade: R.toLower(shade),
            sku,
            name: R.compose(R.toLower(), R.prop('title'))(productDetails),
            type: R.toLower(subcategoryTitle),
            location: 'button',
          });
        }}
        viewType={viewType}
      />
      <div className="gray-boxes">
        <div className="gray-box">
          <button
            type="button"
            className="shop-now"
            onClick={() => {
              setIsMikMak(true);
              trackBuyNowEvent();
            }}
          >
            shop now
          </button>
        </div>
        <div className="gray-box">{vtoButton && vtoButton()}</div>
      </div>
      {isMikMak && mikMakConfig.enabled && (
        <div
          className="buy-now-section-overlay"
          role="button"
          tabIndex="0"
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleOverlayClick(e);
          }}
          onClick={(e) => handleOverlayClick(e)}
        >
          <div className="buy-now-section">
            <MikMakWidget
              wtbid={R.path(['wtbId'], mikMakConfig)}
              selectedSku={selectedSKU}
            />
          </div>
        </div>
      )}
      <ProductDetailsAccordion
        minusIconSrc="/ccx-files/assets/chevron-up.svg"
        plusIconSrc="/ccx-files/assets/chevron-down.svg"
      />
    </div>
  );
};

ProductInfo.propTypes = {
  breadcrumb: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string,
      label: PropTypes.string.isRequired,
    })
  ),
  productDetails: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    averageRating: PropTypes.number,
    numberOfReviews: PropTypes.number,
  }),
  sectionHeadings: PropTypes.shape({
    relatedProducts: PropTypes.shape({
      heading: PropTypes.string,
    }),
    howToSteps: PropTypes.shape({
      heading: PropTypes.string,
    }),
    retailers: PropTypes.shape({
      heading: PropTypes.string,
    }),
  }),
  categoryTitle: PropTypes.string,
  subcategoryTitle: PropTypes.string,
  productSwatch: PropTypes.shape({
    swatches: PropTypes.arrayOf(
      PropTypes.shape({
        sku: PropTypes.string,
        itemName: PropTypes.string,
        skuColor: PropTypes.string,
      })
    ),
  }),
  shapes: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  showRating: PropTypes.bool,
  customRetailer: PropTypes.shape({
    title: PropTypes.string,
    button: PropTypes.shape({
      link: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
    retailer: PropTypes.shape({
      name: PropTypes.string.isRequired,
      currencyCode: PropTypes.string,
      currency: PropTypes.string,
    }),
  }),
  selectedSKU: PropTypes.string.isRequired,
  channelAdvisorWidget: PropTypes.shape({
    url: PropTypes.string.isRequired,
    caPid: PropTypes.string.isRequired,
    lang: PropTypes.string,
  }),
  hidePrice: PropTypes.bool,
  vtoButton: PropTypes.func,
  currentBreakpoint: PropTypes.string.isRequired,
  mikMakConfig: PropTypes.shape({
    appId: PropTypes.string,
    wtbId: PropTypes.string,
    enabled: PropTypes.bool,
  }),
};

export default ProductInfo;
